import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom'; // <-- Import useNavigate
import Header from '../components/Header';
import { Save, CheckCircle } from 'lucide-react';

// --- Reusable Form Components for Consistent Styling ---
const FormInput = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <input 
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
    />
  </div>
);

const FormTextarea = ({ label, ...props }) => (
    <div>
      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
      <textarea 
        {...props}
        rows={3}
        className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
      />
    </div>
);

const SegmentedControl = ({ options, value, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">Title</label>
        <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1 w-full">
            {options.map(option => (
                <button
                    key={option}
                    type="button"
                    onClick={() => onChange(option)}
                    className={`flex-1 px-3 py-1.5 text-sm font-semibold rounded-full transition-colors duration-300 ${
                        value === option
                        ? 'bg-white dark:bg-zinc-700 text-gray-800 dark:text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                    }`}
                >
                    {option}
                </button>
            ))}
        </div>
    </div>
);


export default function Profile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate(); // <-- Initialize useNavigate
  const [formData, setFormData] = useState({
    fullName: '', title: 'MD', address: '', phone: '',
    fax: '', deaNumber: '', npiNumber: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const titles = ["MD", "DO", "NP", "PA", "RN", "Other"];

  const fetchProfile = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    const userDocRef = doc(db, 'users', currentUser.uid);
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      setFormData(prev => ({ ...prev, ...docSnap.data() }));
    }
    setIsLoading(false);
  }, [currentUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleTitleChange = (newTitle) => {
    setFormData(prev => ({ ...prev, title: newTitle }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSaving(true);
    setIsSaved(false);
    const userDocRef = doc(db, 'users', currentUser.uid);
    await setDoc(userDocRef, formData, { merge: true });
    
    setIsSaving(false);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };
  
  if (isLoading) {
    return (
        <div className="bg-[#f4f7f9] dark:bg-black min-h-screen flex items-center justify-center">
            <p className="text-gray-500">Loading Profile...</p>
        </div>
    );
  }

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Your Profile</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">This information may be used for prescriptions.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">Basic Information</h2>
              <FormInput label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} required />
              <SegmentedControl options={titles} value={formData.title} onChange={handleTitleChange} />
            </section>
            
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">Contact Information</h2>
              <FormTextarea label="Practice Address" name="address" value={formData.address} onChange={handleChange} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                <FormInput label="Fax Number" name="fax" type="tel" value={formData.fax} onChange={handleChange} />
              </div>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">Professional Identifiers</h2>
              <FormInput label="NPI Number" name="npiNumber" value={formData.npiNumber} onChange={handleChange} />
              <FormInput label="DEA Registration Number" name="deaNumber" type="password" value={formData.deaNumber} onChange={handleChange} />
            </section>

            <div className="flex justify-end gap-4 pt-6">
              <button 
                type="button" 
                onClick={() => navigate('/dashboard')} // <-- Updated onClick handler
                className="px-8 py-2 bg-slate-200 dark:bg-zinc-700 text-gray-800 dark:text-white rounded-full font-semibold hover:bg-slate-300 dark:hover:bg-zinc-600 transition"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isSaving || isSaved}
                className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                    isSaved 
                    ? 'bg-green-500' 
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : isSaved ? (
                  <> <CheckCircle size={18} /> Saved! </>
                ) : (
                  <> <Save size={18} /> Save Changes </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}